import { Fragment, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, ChevronRight, Clock, User } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { SEOHead } from "@/components/SEOHead";
import { articleSchema, breadcrumbSchema, faqSchema, headingText, isHeadingBlock, linkifyParagraph } from "@/lib/seo";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

// Royal Signature — article: breadcrumbs, auto-linked body, cluster-aware
// related rail, Article + BreadcrumbList structured data.
export default function ArticleRoyal() {
  const { articleId } = useParams();
  const { content } = useContent();
  const { book } = useBooking();
  const articles = content?.articles || [];
  const article = articles.find((a) => a.slug === articleId || a.id === articleId);
  const s = content?.settings || {};
  const dictionary = content?.seo_keywords || [];
  const articlePath = article ? `/blog/${article.slug || article.id}` : "";

  const related = useMemo(() => {
    if (!article) return [];
    const others = articles.filter((a) => a.id !== article.id);
    const sameCluster = article.cluster ? others.filter((a) => a.cluster === article.cluster) : [];
    const sameCategory = others.filter((a) => a.category === article.category && !sameCluster.includes(a));
    const rest = others.filter((a) => !sameCluster.includes(a) && !sameCategory.includes(a));
    return [...sameCluster, ...sameCategory, ...rest].slice(0, 3);
  }, [article, articles]);
  const hasClusterSibling = related.some((r) => article?.cluster && r.cluster === article.cluster);

  // pre-compute auto-linked paragraphs with a FRESH link state per render —
  // idempotent and safe under double-render (StrictMode/concurrent)
  const linkedParagraphs = useMemo(() => {
    if (!article) return [];
    const state = { count: 0, linked: new Set() };
    return (article.content || []).map((p) =>
      isHeadingBlock(p) ? { heading: headingText(p) } : { nodes: linkifyParagraph(p, dictionary, state, articlePath) }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id, JSON.stringify(dictionary), articlePath]);

  if (!content) return <div className="min-h-screen bg-[#FAF8F5]" />;
  if (!article)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAF8F5] px-4 text-center" data-testid="article-not-found">
        <h1 className="font-serif text-4xl font-light text-[#0F0F0F]">Article not found</h1>
        <Link to="/blog" data-testid="article-back-link" className="btn-royal-outline mt-8 px-7 py-3.5 text-[10px]">
          Back to the Journal
        </Link>
      </div>
    );

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Skin Journal", path: "/blog" },
    ...(article.cluster ? [{ name: article.cluster, path: "/blog" }] : []),
    { name: article.title, path: null },
  ];

  return (
    <article className="bg-[#FAF8F5] pb-24 pt-14 lg:pt-20" data-testid="article-page">
      <SEOHead
        title={article.seo_title || `${article.title} | Royal Skin Clinic Dubai`}
        description={article.seo_description || article.excerpt}
        path={articlePath}
        image={article.image}
        jsonLd={[
          articleSchema(article, s, window.location.origin),
          breadcrumbSchema(crumbs, window.location.origin),
          ...((article.faqs || []).length ? [faqSchema(article.faqs)] : []),
        ]}
      />
      <div className="mx-auto max-w-3xl px-4 sm:px-8">
        {/* schema-enabled breadcrumbs */}
        <Reveal>
          <nav aria-label="Breadcrumb" data-testid="article-breadcrumbs" className="flex flex-wrap items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A8580]">
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3 w-3 text-[#C5A059]" aria-hidden="true" />}
                {c.path ? (
                  <Link to={c.path} data-testid={`breadcrumb-${i}`} className="transition-colors hover:text-[#C5A059]">
                    {c.name}
                  </Link>
                ) : (
                  <span className="text-[#4A4A4A]" aria-current="page">{c.name}</span>
                )}
              </span>
            ))}
          </nav>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap items-center gap-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8580]">
            <span className="text-[#C5A059]">{article.category}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{article.read_time}</span>
            {article.published_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <h1 data-testid="article-title" className="mt-5 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#0F0F0F] sm:text-5xl">
            {article.title}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 flex items-center gap-2 text-sm font-medium text-[#4A4A4A]">
            <User className="h-4 w-4 text-[#C5A059]" aria-hidden="true" /> {article.author}
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <img src={article.image} alt={article.title} className="mt-10 w-full rounded-[2rem] object-cover" data-testid="article-image" />
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 space-y-6" data-testid="article-content">
            {linkedParagraphs.map((block, i) => (
              <Fragment key={i}>
                {article.image_2 && i === Math.ceil(linkedParagraphs.length / 2) && (
                  <img src={article.image_2} alt={`${article.title} — clinic detail`} loading="lazy" className="w-full rounded-[2rem] object-cover" data-testid="article-inline-image" />
                )}
                {block.heading ? (
                  <h2 className="pt-4 font-serif text-2xl font-normal text-[#0F0F0F] sm:text-3xl" data-testid={`article-heading-${i}`}>
                    {block.heading}
                  </h2>
                ) : (
                  <p className={`font-light leading-relaxed text-[#4A4A4A] ${i === 0 ? "font-serif text-xl text-[#0F0F0F] sm:text-2xl" : "text-base"}`}>
                    {block.nodes.map((n, j) =>
                      typeof n === "string" ? (
                        n
                      ) : (
                        <Link key={n.key ?? j} to={n.url} data-testid={`auto-link-${j}`} className="font-normal text-[#0F0F0F] underline decoration-[#C5A059]/60 decoration-1 underline-offset-4 transition-colors hover:text-[#C5A059]">
                          {n.link}
                        </Link>
                      )
                    )}
                  </p>
                )}
              </Fragment>
            ))}
          </div>
        </Reveal>
        {(article.faqs || []).length > 0 && (
          <Reveal delay={0.32}>
            <div className="mt-14" data-testid="article-faqs">
              <p className="eyebrow-royal">Common Questions</p>
              <div className="mt-6 space-y-4">
                {article.faqs.map((f, i) => (
                  <div key={i} className="rounded-2xl border border-[#E8E2D9] bg-white p-6" data-testid={`article-faq-${i}`}>
                    <h3 className="font-serif text-lg font-normal text-[#0F0F0F]">{f.question}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-[#4A4A4A]">{f.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}
        <Reveal delay={0.35}>
          {(content?.settings?.section_visibility?.article_cta ?? true) && (
            <div className="mt-14 rounded-[2rem] border border-[#E8E2D9] bg-white p-8 text-center sm:p-10" data-testid="article-cta">
              <p className="eyebrow-royal">Personalised Advice</p>
              <h2 className="mt-3 font-serif text-2xl font-normal text-[#0F0F0F] sm:text-3xl">Your skin deserves a plan, not a guess</h2>
              <p className="mx-auto mt-3 max-w-md text-sm font-light leading-relaxed text-[#8A8580]">
                Book a consultation and get a protocol designed around your features, your skin and your goals.
              </p>
              <button onClick={() => book()} data-testid="article-book-button" className="btn-royal mt-7 px-8 py-4 text-[10px]">
                Book Your Consultation
              </button>
            </div>
          )}
        </Reveal>

        {related.length > 0 && (
          <div className="mt-16" data-testid="article-related">
            <p className="eyebrow-royal">{hasClusterSibling ? `More in ${article.cluster}` : "Keep Reading"}</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-3">
              {related.map((a) => (
                <Link key={a.id} to={`/blog/${a.slug || a.id}`} data-testid={`article-related-${a.id}`} className="group block">
                  <div className="overflow-hidden rounded-xl">
                    <img src={a.image} alt={a.title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <h3 className="mt-3 font-serif text-lg font-normal leading-snug text-[#0F0F0F] transition-colors group-hover:text-[#C5A059]">{a.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Reveal delay={0.4}>
          <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-[#E8E2D9] pt-8" data-testid="article-share">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8580]">Share this essay</span>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${article.title} — Royal Skin Clinic ${window.location.origin}${articlePath}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-share-whatsapp"
              className="inline-flex items-center gap-1.5 rounded-[5px] border border-[#E8E2D9] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0F0F0F] transition-colors hover:border-[#25D366] hover:text-[#25D366]"
            >
              WhatsApp
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}${articlePath}`);
                toast.success("Link copied — share it anywhere");
              }}
              data-testid="article-share-copy"
              className="inline-flex items-center gap-1.5 rounded-[5px] border border-[#E8E2D9] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#0F0F0F] transition-colors hover:border-[#C5A059] hover:text-[#C5A059]"
            >
              Copy Link
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.45}>
          <Link to="/blog" data-testid="article-back-to-journal" className="royal-link mt-10">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> All Essays
          </Link>
        </Reveal>
      </div>
    </article>
  );
}
